import { useEffect, useState } from "react";

/**
 * Landing page for Upstox's post-login redirect (OAuth authorization-code flow).
 *
 * Upstox redirects here with `?code=XXX`. This page posts the code back to the
 * opener window via postMessage; the opener (BrokersPage's Upstox form or
 * Refresh button) does the actual exchange server-side via
 * /broker-connections/upstox/exchange-token (needs api_secret, which never
 * leaves the opener's memory / the backend's encrypted store).
 *
 * No authentication required — the code alone is useless without the secret,
 * and it's single-use with a short TTL.
 */
export default function UpstoxCallbackPage() {
  const [status, setStatus] = useState<string>("Reading authorization code…");

  useEffect(() => {
    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");
    const errorMsg =
      url.searchParams.get("error_description") ?? url.searchParams.get("error");
    if (errorMsg) {
      setStatus(`Upstox login error: ${errorMsg}`);
      window.opener?.postMessage(
        { type: "upstox-code-error", error: errorMsg },
        window.location.origin,
      );
      return;
    }
    if (!code) {
      setStatus("No code in URL — did Upstox redirect here directly?");
      return;
    }
    if (!window.opener) {
      setStatus("Opened directly (no parent window). Copy this code manually:");
      return;
    }
    window.opener.postMessage(
      { type: "upstox-code", code },
      window.location.origin,
    );
    setStatus("Code sent back to the broker form. You can close this tab.");
  }, []);

  const url = new URL(window.location.href);
  const code = url.searchParams.get("code");

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-2">
      <div className="max-w-lg bg-surface rounded-lg border border-border shadow-sm p-8">
        <h1 className="text-lg font-semibold">Upstox login complete</h1>
        <p className="mt-3 text-sm text-fg-subtle">{status}</p>
        {!window.opener && code && (
          <pre className="mt-3 bg-surface-2 rounded px-3 py-2 text-xs font-mono break-all">
            {code}
          </pre>
        )}
        <button
          type="button"
          onClick={() => window.close()}
          className="mt-5 text-sm text-up-700 dark:text-up-400 hover:underline"
        >
          Close window
        </button>
      </div>
    </div>
  );
}
